/**
 * Which questions are asking her to show rather than tell.
 *
 * Kept narrow, and kept apart from the rest of the routing, because every one
 * of these runs ahead of the platform's catalogue: a pattern that is too loose
 * here takes a question about the records ("show me the overdue work orders")
 * and answers it with a tour. Each is pinned by tests that include the
 * questions it must leave alone.
 */

export type ShowIntent =
  | { kind: "stop" }
  | { kind: "site-tour"; full: boolean }
  | { kind: "screen-tour" }
  | { kind: "named-tour"; words: string }
  | { kind: "review" }
  | { kind: "walk-review" }
  | { kind: "review-item"; n: number }
  | { kind: "how-to"; goal: string };

const clean = (q: string) => q.toLowerCase().trim().replace(/^sky,?\s+/, "").replace(/[?.!]+$/, "");

export function showIntent(q: string): ShowIntent | null {
  const t = clean(q);

  if (/^(stop|end|cancel|exit|quit)( the| this)? (tour|walkthrough|walk through|tutorial|demo)\b/.test(t)) return { kind: "stop" };

  const item = t.match(/^show me (?:item|finding|number) (\d{1,2})$/);
  if (item) return { kind: "review-item", n: Number(item[1]) };

  if (
    /\bwalk me through (the |those |these )?(critical|findings|review|items|them)\b/.test(t) ||
    /^show me (them|those|the (critical items|findings))$/.test(t)
  )
    return { kind: "walk-review" };

  if (
    /\b(review|scan|check|go through|sweep)\b.{0,30}\b(critical|urgent items|attention|the system|across the system|everything|critical items)\b/.test(t) ||
    /\bsuggest (some |me )?(tasks|what to do|priorities|next steps)\b/.test(t) ||
    /* "what should I do / look at", "what needs my attention" are the triage answer's (local.ts) */
    /\bwhat should i (work on|focus on)( today| first| now)?$/.test(t) ||
    /^critical items$/.test(t)
  )
    return { kind: "review" };

  if (
    /\b(show me around|walk me through|tour of|guide me through|tutorial (for|on)|explain)\b.{0,12}\b(this|the current|current)\s+(screen|page|desk|board|tab)\b/.test(t) ||
    /^(show me around|give me a tour) here$/.test(t)
  )
    return { kind: "screen-tour" };

  const how = t.match(/^(?:show me how(?: do i| can i| to)?|teach me how to|how do i|how can i|how would i)\s+(.{4,})$/);
  if (how) return { kind: "how-to", goal: how[1].replace(/^to\s+/, "") };

  const tourish =
    /\b(tutorial|walk\s?through|walk me through|show me around|give me a tour|take me on a tour|tour|teach me|orient me|onboard me)\b/.test(t);
  if (tourish) {
    const named = t.match(/\b(?:tour of|around|through|me on|tutorial (?:for|on))\s+(?:the\s+)?(.+?)(?:\s+(?:suite|screen|page|module|desk))?$/);
    const words = named?.[1] ?? "";
    if (words && !/^(the )?(app|platform|system|site|everything|it|this|whole thing|all of it)$/.test(words) && !/\b(full|whole|complete|entire)\b/.test(words))
      return { kind: "named-tour", words };
    return { kind: "site-tour", full: /\b(full|whole|complete|entire|every screen|all (the )?screens|long)\b/.test(t) };
  }
  return null;
}
